/**
 * LiveControlBar — the fixed top chrome for a co-driven demo: connection status, the
 * shared-control reminder, (host only) the shareable guest link, and End.
 * Presentation only.
 */
import { useState } from "react";
import type { LiveRole } from "./live";
import { guestLink } from "./live";

interface Props {
  role: LiveRole;
  peerConnected: boolean;
  code: string;
  onEnd: () => void;
}

export function LiveControlBar({ role, peerConnected, code, onEnd }: Props) {
  const [copied, setCopied] = useState(false);
  const copyLink = () => {
    navigator.clipboard?.writeText(guestLink(code)).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      },
      () => {},
    );
  };

  return (
    <div className="fixed inset-x-0 top-0 z-[80] flex items-center justify-between gap-3 border-b border-[var(--color-border-gold)] bg-[var(--color-bg-deep)]/95 px-4 py-2 backdrop-blur">
      <div className="flex min-w-0 items-center gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-gold)] px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-bg-deep)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-bg-deep)]" />
          Live demo
        </span>
        <span
          className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.12em] ${
            peerConnected ? "text-[var(--color-text-muted)]" : "text-[var(--color-text-ghost)]"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${peerConnected ? "bg-emerald-400" : "bg-amber-400"}`}
          />
          {peerConnected ? "Connected" : "Waiting for the other person…"}
        </span>
        {peerConnected && (
          <span className="hidden truncate font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--color-gold-soft)] sm:inline">
            You're both in control
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {role === "host" && (
          <button
            onClick={copyLink}
            className="rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)]"
          >
            {copied ? "Link copied ✓" : "Copy guest link"}
          </button>
        )}
        <button
          onClick={onEnd}
          className="rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)] transition-colors hover:border-red-400 hover:text-red-300"
        >
          End
        </button>
      </div>
    </div>
  );
}
